import * as React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { RichTextEditor } from './components/RichTextEditor';
import { sanitizeTelegramHtml } from './utils';
import { TextWrap } from 'lucide-react';

interface City {
  citiesId: number;
  name?: string;
}

interface Settings {
  catalogLink: string;
  selectCategoryText: string;
  partyCakes: string;
  chatText: string;
  aboutUsText: string;
  priceText: string;
  backToMainMenu: string;
  cityIdBiggerPrice: string[];
}

const API_URL = import.meta.env.VITE_API_URL;

const ViberSettings = () => {
  const [settings, setSettings] = React.useState<Settings>({
    catalogLink: '',
    selectCategoryText: '',
    partyCakes: '',
    chatText: '',
    aboutUsText: '',
    priceText: '',
    backToMainMenu: '',
    cityIdBiggerPrice: [],
  });

  const [cities, setCities] = React.useState<City[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saved, setSaved] = React.useState(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const [googleSyncLoading, setGoogleSyncLoading] = React.useState(false);

  React.useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/viber-settings`).then((r) => r.json()),
      fetch(`${API_URL}/cities`).then((r) => r.json()),
    ]).then(([settingsRes, citiesRes]) => {
      const s = settingsRes?.settings || {};
      setSettings({
        catalogLink: s.catalogLink || '',
        selectCategoryText: s.selectCategoryText || '',
        partyCakes: s.partyCakes || '',
        chatText: s.chatText || '',
        aboutUsText: s.aboutUsText || '',
        priceText: s.priceText || '',
        backToMainMenu: s.backToMainMenu || '',
        cityIdBiggerPrice: Array.isArray(s.cityIdBiggerPrice)
          ? s.cityIdBiggerPrice.map(String)
          : JSON.parse(s?.cityIdBiggerPrice || '[]').map(String),
      });

      setCities(Array.isArray(citiesRes?.cities) ? citiesRes.cities : []);
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    await fetch(`${API_URL}/viber-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...settings,
        selectCategoryText: sanitizeTelegramHtml(settings.selectCategoryText),
        partyCakes: sanitizeTelegramHtml(settings.partyCakes),
        chatText: sanitizeTelegramHtml(settings.chatText),
        aboutUsText: sanitizeTelegramHtml(settings.aboutUsText),
        priceText: sanitizeTelegramHtml(settings.priceText),
      }),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (loading) return null;

  return (
    <Card sx={{ maxWidth: 900 }}>
      <CardHeader title="Налаштування Viber" />
      <CardContent>
        <Box
          style={{
            whiteSpace: 'pre-line',
            border: '2px solid #75009c',
            padding: 10,
            marginBottom: 15,
            borderRadius: 10,
          }}
        >
          {`До уваги користувача:
          - Viber може неккоректно відображати декілька форматувань. Рекомендується робити тест або жирним, або курсивом.
          - Viber не підтримує підкреслений текст, замість нього він використовує закреслений текст.
          - За стандартом посилання відображаються у повідомленні як [запит](посилання)`}
        </Box>
        <Stack spacing={3}>
          <TextField
            label="Посилання на каталог"
            value={settings.catalogLink}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, catalogLink: e.target.value }))
            }
            fullWidth
          />

          <RichTextEditor
            value={settings.selectCategoryText}
            onChange={(html) =>
              setSettings((prev) => ({ ...prev, selectCategoryText: html }))
            }
            label='Текст, який відображається коли користувач натискає "Обрати товар"'
            minRows={3}
          />

          <RichTextEditor
            value={settings.partyCakes}
            onChange={(html) =>
              setSettings((prev) => ({ ...prev, partyCakes: html }))
            }
            label='Текст, який відображається коли користувач натискає "Святкові торти"'
            minRows={4}
          />

          <RichTextEditor
            value={settings.chatText}
            onChange={(html) =>
              setSettings((prev) => ({ ...prev, chatText: html }))
            }
            label='Текст, який відображається коли користувач натискає "Чат"'
            minRows={3}
          />

          <RichTextEditor
            value={settings.aboutUsText}
            onChange={(html) =>
              setSettings((prev) => ({ ...prev, aboutUsText: html }))
            }
            label='Текст, який відображається коли користувач натискає "Про нас"'
            minRows={4}
          />

          <RichTextEditor
            value={settings.priceText}
            onChange={(html) =>
              setSettings((prev) => ({ ...prev, priceText: html }))
            }
            label='Текст, який відображається коли користувач натискає "Оплата"'
            minRows={4}
          />

          <Button variant="contained" onClick={handleSave}>
            Зберегти
          </Button>
          {saved && <Typography color="success.main">Все збережено</Typography>}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ViberSettings;
